import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Card, Typography, Grid, Button, Stack, Input, Select, Option, Chip } from "@mui/joy";
import { Save, RotateCcw, ClipboardCheck, PackageCheck, Clock } from "lucide-react";
import { debounce } from "lodash";
import moment from "moment";

import { useInspectionAttributes, useMeasurement } from "../../hooks/query";
import { useProject } from "../../hooks";

import { ShingleType } from "../Inspection/components/ShingleType";
import { ColorSelection } from "../Inspection/components/ColorSelection";
import { RoofSlopes } from "../Inspection/components/RoofSlopes";
import { TearOff } from "../Inspection/components/TearOff";
import { UnderlaymentType } from "../Inspection/components/UnderlaymentType";
import { StarterType } from "../Inspection/components/StarterType";
import { RidgeCapProfile } from "../Inspection/components/RidgeCapProfile";
import { VentilationType } from "../Inspection/components/VentilationType";
import { VentilationPainted } from "../Inspection/components/VentilationPainted";
import { FurnaceVentSize } from "../Inspection/components/FurnaceVentSize";
import { EaveDripEdge } from "../Inspection/components/EaveDripEdge";
import { RakeDripEdge } from "../Inspection/components/RakeDripEdge";
import { ValleyType } from "../Inspection/components/ValleyType";
import { EvaporativeCooler } from "../Inspection/components/EvaporativeCooler";
import { GutterGuardType } from "../Inspection/components/GutterGuardType";
import { BuildingStories } from "../Inspection/components/BuildingStories";
import { ChimneyList } from "../Inspection/components/ChimneyList";
import { SkylightList } from "../Inspection/components/SkylightList";
import { ItemPaintedDropdown } from "../Inspection/components/ItemPaintedDropdown";
import { PropertyPhotos } from "../Inspection/components/PropertyPhotos";
import { InitialInspectionReport } from "../Inspection/components/InitialInspectionReport";
import { ClaimsEstimateReport } from "../Inspection/components/ClaimsEstimateReport";
import { TaskActionComponent } from "../Tasks/components";

type ActionType = "R&R" | "Remove" | "Install" | "D&R";
const ACTION_OPTIONS: ActionType[] = ["R&R", "Remove", "Install", "D&R"];
const ACTION_COLORS: Record<ActionType, "primary" | "danger" | "success" | "warning"> = {
    "R&R": "primary",
    Remove: "danger",
    Install: "success",
    "D&R": "warning",
};

interface IInspectionV2 {
    activeTask: any;
    attributes: any[];
    setAttributes: (e: any) => void;
    measurements: any[];
    setMeasurements: (e: any) => void;
    onComplete: () => void;
}

export default function InspectionV2(props: IInspectionV2) {
    const { activeTask, attributes, setAttributes, measurements, setMeasurements, onComplete } = props;
    const { project } = useProject();
    const { useUpdateSingleAttribute } = useInspectionAttributes();
    const updateAttributeSvc = useUpdateSingleAttribute();
    const { useUpdateSingleMeasurement } = useMeasurement();
    const updateMeasurementSvc = useUpdateSingleMeasurement();

    // --- Visual measurement state ---
    const [visualData, setVisualData] = useState<any[]>([]);
    const [actions, setActions] = useState<Record<string, ActionType>>({
        "Roof Vent": "R&R",
        "Pipe Jack": "R&R",
        "Split Boots": "R&R",
        "Furnace Vents": "R&R",
        "Bathroom Vents": "R&R",
        "Dryer Vents": "R&R",
        Chimney: "R&R",
        Skylights: "R&R",
        "Step Flashing": "Install",
        "Kick-out Diverter": "R&R",
        "Ice & Water Barrier": "Install",
        "Cornice Returns": "R&R",
        "Cornice Strip": "R&R",
        Sheathing: "R&R",
        "Fascia Board": "R&R",
        Soffit: "R&R",
        "Gutter Apron": "R&R",
        "Gutter/Downspout": "R&R",
        "Paint Gutter": "Install",
    });

    const visualItems = [
        "Roof Vent",
        "Pipe Jack",
        "Furnace Vents",
        "Bathroom Vents",
        "Dryer Vents",
        "Split Boots",
        "Chimney",
        "Skylights",
        "High Roof",
        "Ice & Water Barrier",
        "Step Flashing",
        "Kick-out Diverter",
    ];

    const processVisualData = useCallback(() => {
        if (!measurements || measurements.length === 0) return;
        const data = visualItems.map((name) => {
            const entry = measurements.find((m: any) => m.MeasurementType === name);
            if (!entry) return { name, actual: 0, id: null };
            return { name, actual: Number(entry.ActualValue), id: entry.Id };
        });
        setVisualData(data);
    }, [measurements]);

    useEffect(() => {
        processVisualData();
    }, [processVisualData]);

    const debouncedMeasurementUpdate = useRef(
        debounce(async (recordId: string, newValue: number) => {
            try {
                await updateMeasurementSvc.mutateAsync({ Id: recordId, ActualValue: Number(newValue) });
                setMeasurements((prev: any[]) =>
                    prev.map((m) => (m.Id === recordId ? { ...m, ActualValue: Number(newValue) } : m)),
                );
            } catch (error) {
                console.error(error);
            }
        }, 500),
    ).current;

    useEffect(
        () => () => {
            debouncedMeasurementUpdate.cancel();
        },
        [debouncedMeasurementUpdate],
    );

    const handleVisualInput = (itemName: string, value: number) => {
        setVisualData((prev) =>
            prev.map((item) => {
                if (item.name === itemName) {
                    if (item.id) debouncedMeasurementUpdate(item.id, value);
                    return { ...item, actual: value };
                }
                return item;
            }),
        );
    };

    const getVisualItem = (name: string) => visualData.find((v) => v.name === name);

    // --- Attribute helpers ---
    const getAttrValue = (name: string) => attributes?.find((attr: any) => attr.AttributeName === name)?.AttributeValue;

    const handleUpdate = async (value: string, attributeName: string) => {
        try {
            if (!project?.id) return;
            const attribute = attributes?.find((attr: any) => attr.AttributeName === attributeName);
            const attributeId = attribute?.Id;
            const updateParams: any = { Value: value };
            if (attributeId) {
                updateParams.Id = attributeId;
            } else {
                updateParams.ProjectId = project.id;
                updateParams.AttributeName = attributeName;
            }
            const response = await updateAttributeSvc.mutateAsync(updateParams);
            if (attributeId) {
                setAttributes((prev: any[]) =>
                    prev.map((attr: any) =>
                        attr.AttributeName === attributeName ? { ...attr, AttributeValue: value } : attr,
                    ),
                );
            } else {
                const newAttr = {
                    Id: response?.Id || attribute?.Id,
                    AttributeName: attributeName,
                    AttributeValue: value,
                };
                setAttributes((prev: any[]) => {
                    const exists = prev.some((a: any) => a.AttributeName === attributeName);
                    if (!exists) return [...prev, newAttr];
                    return prev.map((a: any) => (a.AttributeName === attributeName ? { ...a, ...newAttr } : a));
                });
            }
        } catch (error) {
            console.error(`Error updating ${attributeName}:`, error);
        }
    };

    // Auto-default Starter Type based on Shingle Type
    useEffect(() => {
        const shingleType = getAttrValue("Shingle Type");
        const currentStarter = getAttrValue("Starter Type");

        if (shingleType === "Shake Look") {
            if (currentStarter !== "Laminated Double Layer") {
                handleUpdate("Laminated Double Layer", "Starter Type");
            }
        } else if (shingleType && shingleType !== "None") {
            // Default others to Universal Starter if a shingle is selected
            if (currentStarter !== "Universal Starter") {
                handleUpdate("Universal Starter", "Starter Type");
            }
        }
    }, [getAttrValue("Shingle Type")]);

    const sectionHeaderSx = {
        color: "text.tertiary",
        mb: 1.5,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontSize: "11px",
        fontWeight: "xl",
    };
    const cardSx = { p: 2, bgcolor: "background.surface", boxShadow: "none" };

    return (
        <Box>
            <Box mt={2} ml={1} mr={1}>
                {/* TASK HEADER — same as V1 */}
                <Typography startDecorator={<PackageCheck size={16} />} level="body-sm" gutterBottom>
                    Task
                </Typography>
                <Typography level="h4" sx={{ fontWeight: 500, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
                    Initial Roof Inspection
                </Typography>

                <Box mt={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
                    <Chip
                        color={activeTask?.Status === "COMPLETED" ? "success" : "warning"}
                        sx={{ fontSize: 12, border: "1px solid", borderColor: "divider" }}
                    >
                        {activeTask?.Status}
                    </Chip>
                    <Chip color="neutral" variant="outlined" sx={{ fontSize: 12 }} startDecorator={<Clock size={14} />}>
                        Created On: {moment(activeTask?.CreatedAt).format("MM/DD/YYYY")}
                    </Chip>
                </Box>

                {/* DESCRIPTION */}
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    <Typography level="body-sm" sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                        Description
                    </Typography>
                    <Typography level="body-md" sx={{ color: "text.primary" }}>
                        The primary goal of an Initial Roof Inspection is to assess the overall condition of a roof,
                        identify potential issues, document findings, and prepare a detailed report. This inspection
                        serves as a baseline evaluation for maintenance, repair, or replacement decisions.
                    </Typography>
                </Box>

                {/* PHOTOS — directly, no card wrapper */}
                <Box mt={2}>
                    <Typography level="body-sm" sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                        Photos
                    </Typography>
                    <Typography level="body-md" sx={{ color: "text.primary" }}>
                        Upload clear and detailed photos taken during the initial roof inspection to document its
                        current condition. These images will help assess any existing damages, structural concerns, or
                        areas requiring maintenance.
                    </Typography>
                    <Box mt={1}>
                        <PropertyPhotos taskDetails={activeTask} />
                    </Box>
                </Box>

                {/* WORKSHEET SECTIONS */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 4, mt: 4 }}>
                    {/* GROUP: SHINGLE & ROOFING */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Shingle & Roofing
                        </Typography>
                        <Card variant="outlined" sx={cardSx}>
                            <Grid container spacing={2}>
                                <Grid xs={12} sm={6} md={4}>
                                    <ShingleType
                                        value={getAttrValue("Shingle Type")}
                                        onChange={(v) => handleUpdate(v, "Shingle Type")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <ColorSelection
                                        value={getAttrValue("Roof Color")}
                                        onChange={(v) => handleUpdate(v, "Roof Color")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <TearOff
                                        value={getAttrValue("Tear Off Layer")}
                                        onChange={(v) => handleUpdate(v, "Tear Off Layer")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <UnderlaymentType
                                        value={getAttrValue("Underlayment Type")}
                                        onChange={(v) => handleUpdate(v, "Underlayment Type")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <StarterType
                                        value={getAttrValue("Starter Type")}
                                        onChange={(v) => handleUpdate(v, "Starter Type")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <RidgeCapProfile
                                        value={getAttrValue("Ridge Cap Profile")}
                                        onChange={(v) => handleUpdate(v, "Ridge Cap Profile")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <BuildingStories
                                        value={getAttrValue("Stories")}
                                        onChange={(v) => handleUpdate(v, "Stories")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <CountWithAction
                                        label="High Roof (SQ)"
                                        item={getVisualItem("High Roof") || { name: "High Roof", actual: 0, id: null }}
                                        action={actions["High Roof"]}
                                        onQtyChange={(v) => handleVisualInput("High Roof", v)}
                                        onActionChange={(a) => setActions((prev) => ({ ...prev, "High Roof": a }))}
                                        unit="SQ"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <CountWithAction
                                        label="Ice & Water Barrier"
                                        item={
                                            getVisualItem("Ice & Water Barrier") || {
                                                name: "Ice & Water Barrier",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Ice & Water Barrier"]}
                                        onQtyChange={(v) => handleVisualInput("Ice & Water Barrier", v)}
                                        onActionChange={(a) =>
                                            setActions((prev) => ({ ...prev, "Ice & Water Barrier": a }))
                                        }
                                        unit="SF"
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <RoofSlopes
                                        value={getAttrValue("Roof Pitch")}
                                        onChange={(v) => handleUpdate(v, "Roof Pitch")}
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>

                    {/* GROUP: VENTILATION */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Ventilation
                        </Typography>
                        <Card variant="outlined" sx={cardSx}>
                            <Grid container spacing={2} alignItems="flex-end">
                                <Grid xs={12} sm={6} md={4}>
                                    <VentilationType
                                        value={getAttrValue("Ventilation Type")}
                                        onChange={(v) => handleUpdate(v, "Ventilation Type")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <VentilationPainted
                                        value={getAttrValue("Ventilation Painted")}
                                        onChange={(v) => handleUpdate(v, "Ventilation Painted")}
                                        showNA={getAttrValue("Ventilation Type") === "Ridge Vent"}
                                    />
                                </Grid>
                                {/* Conditionally show LF or Count based on vent type */}
                                {getAttrValue("Ventilation Type") === "Ridge Vent" ? (
                                    <Grid xs={12} sm={6} md={4}>
                                        <CountWithAction
                                            label="Ridge Vent (LF)"
                                            item={getVisualItem("Roof Vent")}
                                            action={actions["Roof Vent"]}
                                            onQtyChange={(v) => handleVisualInput("Roof Vent", v)}
                                            onActionChange={(a) => setActions((prev) => ({ ...prev, "Roof Vent": a }))}
                                            unit="LF"
                                        />
                                    </Grid>
                                ) : (
                                    <Grid xs={12} sm={6} md={4}>
                                        <CountWithAction
                                            label="Box Vents"
                                            item={getVisualItem("Roof Vent")}
                                            action={actions["Roof Vent"]}
                                            onQtyChange={(v) => handleVisualInput("Roof Vent", v)}
                                            onActionChange={(a) => setActions((prev) => ({ ...prev, "Roof Vent": a }))}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Card>
                    </Box>

                    {/* GROUP: PENETRATIONS */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Penetrations
                        </Typography>
                        <Card variant="outlined" sx={cardSx}>
                            <Grid container spacing={2}>
                                <Grid xs={12} sm={6} md={4}>
                                    <FurnaceVentSize
                                        value={getAttrValue("Furnace Vent Size")}
                                        onChange={(v) => handleUpdate(v, "Furnace Vent Size")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                        <CountWithAction
                                            label="Furnace Vents"
                                            item={getVisualItem("Furnace Vents")}
                                            action={actions["Furnace Vents"]}
                                            onQtyChange={(v) => handleVisualInput("Furnace Vents", v)}
                                            onActionChange={(a) => setActions((prev) => ({ ...prev, "Furnace Vents": a }))}
                                        />
                                        <ItemPaintedDropdown 
                                            value={getAttrValue("Furnace Vents Painted")} 
                                            onChange={(v) => handleUpdate(v, "Furnace Vents Painted")} 
                                        />
                                    </Box>
                                </Grid>
                                {(["Pipe Jack", "Split Boots"] as const).map((name) => (
                                    <Grid xs={12} sm={6} md={4} key={name}>
                                        <CountWithAction
                                            label={name}
                                            item={getVisualItem(name)}
                                            action={actions[name]}
                                            onQtyChange={(v) => handleVisualInput(name, v)}
                                            onActionChange={(a) => setActions((prev) => ({ ...prev, [name]: a }))}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Card>
                    </Box>

                    {/* GROUP: DRIP EDGE, FLASHING & VALLEY */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Drip Edge, Flashing & Valley
                        </Typography>
                        <Card variant="outlined" sx={cardSx}>
                            <Grid container spacing={2}>
                                <Grid xs={12} sm={6} md={4}>
                                    <EaveDripEdge
                                        value={getAttrValue("Eave Drip Edge")}
                                        onChange={(v) => handleUpdate(v, "Eave Drip Edge")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <RakeDripEdge
                                        value={getAttrValue("Rake Drip Edge")}
                                        onChange={(v) => handleUpdate(v, "Rake Drip Edge")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <ValleyType
                                        value={getAttrValue("Valley Type")}
                                        onChange={(v) => handleUpdate(v, "Valley Type")}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Step Flashing"
                                        item={
                                            getVisualItem("Step Flashing") || {
                                                name: "Step Flashing",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Step Flashing"]}
                                        onQtyChange={(v) => handleVisualInput("Step Flashing", v)}
                                        onActionChange={(a) => setActions((prev) => ({ ...prev, "Step Flashing": a }))}
                                        unit="LF"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Kick-out Diverter"
                                        item={
                                            getVisualItem("Kick-out Diverter") || {
                                                name: "Kick-out Diverter",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Kick-out Diverter"]}
                                        onQtyChange={(v) => handleVisualInput("Kick-out Diverter", v)}
                                        onActionChange={(a) =>
                                            setActions((prev) => ({ ...prev, "Kick-out Diverter": a }))
                                        }
                                    />
                                </Grid>

                            </Grid>
                        </Card>
                    </Box>

            {/* GROUP: CHIMNEY */}
            <Box>
                <Typography level="title-sm" sx={sectionHeaderSx}>
                    Chimney
                </Typography>
                <Card variant="outlined" sx={cardSx}>
                    <ChimneyList value={getAttrValue("Chimney Details")} onChange={(v) => handleUpdate(v, "Chimney Details")} />
                </Card>
            </Box>

            {/* GROUP: SKYLIGHT */}
            <Box>
                <Typography level="title-sm" sx={sectionHeaderSx}>
                    Skylight
                </Typography>
                <Card variant="outlined" sx={cardSx}>
                    <SkylightList value={getAttrValue("Skylight Details")} onChange={(v) => handleUpdate(v, "Skylight Details")} />
                </Card>
            </Box>

                    {/* GROUP: EQUIPMENT & GUTTERS */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Equipment & Gutters
                        </Typography>
                        <Card variant="outlined" sx={cardSx}>
                            <Grid container spacing={2}>
                                <Grid xs={12} sm={6} md={3}>
                                    <EvaporativeCooler
                                        value={getAttrValue("Has Evaporative Cooler")}
                                        onChange={(v) => handleUpdate(v, "Has Evaporative Cooler")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <GutterGuardType
                                        value={getAttrValue("Gutter Guard Type")}
                                        onChange={(v) => handleUpdate(v, "Gutter Guard Type")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Gutter/Downspout"
                                        item={
                                            getVisualItem("Gutter/Downspout") || {
                                                name: "Gutter/Downspout",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Gutter/Downspout"]}
                                        onQtyChange={(v) => handleVisualInput("Gutter/Downspout", v)}
                                        onActionChange={(a) =>
                                            setActions((prev) => ({ ...prev, "Gutter/Downspout": a }))
                                        }
                                        unit="LF"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Paint Gutter"
                                        item={
                                            getVisualItem("Paint Gutter") || {
                                                name: "Paint Gutter",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Paint Gutter"]}
                                        onQtyChange={(v) => handleVisualInput("Paint Gutter", v)}
                                        onActionChange={(a) => setActions((prev) => ({ ...prev, "Paint Gutter": a }))}
                                        unit="LF"
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>

                    {/* GROUP: ANCILLARY / TRIM */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Ancillary / Trim
                        </Typography>
                        <Card variant="outlined" sx={cardSx}>
                            <Grid container spacing={2}>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Cornice Returns"
                                        item={
                                            getVisualItem("Cornice Returns") || {
                                                name: "Cornice Returns",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Cornice Returns"]}
                                        onQtyChange={(v) => handleVisualInput("Cornice Returns", v)}
                                        onActionChange={(a) =>
                                            setActions((prev) => ({ ...prev, "Cornice Returns": a }))
                                        }
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Cornice Strip"
                                        item={
                                            getVisualItem("Cornice Strip") || {
                                                name: "Cornice Strip",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Cornice Strip"]}
                                        onQtyChange={(v) => handleVisualInput("Cornice Strip", v)}
                                        onActionChange={(a) =>
                                            setActions((prev) => ({ ...prev, "Cornice Strip": a }))
                                        }
                                        unit="LF"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Fascia Board"
                                        item={
                                            getVisualItem("Fascia Board") || {
                                                name: "Fascia Board",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Fascia Board"]}
                                        onQtyChange={(v) => handleVisualInput("Fascia Board", v)}
                                        onActionChange={(a) =>
                                            setActions((prev) => ({ ...prev, "Fascia Board": a }))
                                        }
                                        unit="LF"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Soffit"
                                        item={getVisualItem("Soffit") || { name: "Soffit", actual: 0, id: null }}
                                        action={actions["Soffit"]}
                                        onQtyChange={(v) => handleVisualInput("Soffit", v)}
                                        onActionChange={(a) => setActions((prev) => ({ ...prev, Soffit: a }))}
                                        unit="SF"
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Gutter Apron"
                                        item={
                                            getVisualItem("Gutter Apron") || {
                                                name: "Gutter Apron",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Gutter Apron"]}
                                        onQtyChange={(v) => handleVisualInput("Gutter Apron", v)}
                                        onActionChange={(a) =>
                                            setActions((prev) => ({ ...prev, "Gutter Apron": a }))
                                        }
                                        unit="LF"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Sheathing / OSB"
                                        item={getVisualItem("Sheathing") || { name: "Sheathing", actual: 0, id: null }}
                                        action={actions["Sheathing"]}
                                        onQtyChange={(v) => handleVisualInput("Sheathing", v)}
                                        onActionChange={(a) => setActions((prev) => ({ ...prev, Sheathing: a }))}
                                        unit="SF"
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>
                </Box>

                {/* DOCUMENTS */}
                <Box mt={4}>
                    <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                        Documents Review
                    </Typography>
                    <Typography level={"body-md"} sx={{ color: "text.primary" }} gutterBottom>
                        Review and download the generated inspection reports and estimates.
                    </Typography>
                    <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                        <InitialInspectionReport />
                        <ClaimsEstimateReport />
                    </Box>
                </Box>

                {/* TASK ACTION — complete task */}
                <TaskActionComponent taskName="Initial Inspection" onComplete={onComplete} isNextEligible={true} />
            </Box>
        </Box>
    );
}

/** Count input with an action override chip — visually matches Select/Input fields */
function CountWithAction({
    label,
    item,
    action,
    onQtyChange,
    onActionChange,
    unit,
}: {
    label: string;
    item: any;
    action: ActionType;
    onQtyChange: (v: number) => void;
    onActionChange: (a: ActionType) => void;
    unit?: string;
}) {
    return (
        <Box flex={1}>
            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>
                {label}
            </Typography>
            <Stack direction="row" spacing={1}>
                <Input
                    size="sm"
                    type="number"
                    placeholder="0"
                    value={item?.actual || ""}
                    onChange={(e) => onQtyChange(Math.max(0, Number(e.target.value) || 0))}
                    slotProps={{ input: { min: 0 } }}
                    endDecorator={
                        unit ? (
                            <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: "bold" }}>
                                {unit}
                            </Typography>
                        ) : undefined
                    }
                    sx={{ flex: 1 }}
                />
                <Select
                    size="sm"
                    variant="soft"
                    value={action}
                    onChange={(_e, val) => val && onActionChange(val as ActionType)}
                    slotProps={{ listbox: { sx: { zIndex: 10001 } } }}
                    sx={{ minWidth: 70 }}
                    renderValue={(selected) => (
                        <Chip
                            size="sm"
                            variant="soft"
                            color={ACTION_COLORS[selected?.value as ActionType] || "neutral"}
                            sx={{ fontSize: "9px", fontWeight: "bold" }}
                        >
                            {selected?.label}
                        </Chip>
                    )}
                >
                    {ACTION_OPTIONS.map((opt) => (
                        <Option key={opt} value={opt}>
                            {opt}
                        </Option>
                    ))}
                </Select>
            </Stack>
        </Box>
    );
}
